/**
 * Created: 2026-10-21
 * Initial commit: Coach or creator edits and cancels appointments in the web UI
 *
 * Dialog to edit who is on an INVITE coaching appointment and how many each
 * brings, the web counterpart of the mini coach roster page. Submits the whole
 * new roster to `updateCoachingAppointment`. Candidates are the live attendees
 * plus `schedulableStudents` for the course on the appointment's date; the
 * server re-checks hours and seats.
 */

import { gql } from '@apollo/client'
import { useMutation, useQuery } from '@apollo/client/react'
import { message } from '@homemaster/jslib'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  Typography,
} from '@mui/material'
import {
  hoursCapOf,
  maxHeadcountOf as maxHeadcountFor,
} from '@tc-app/shared/coaching/appointment'
import { useState } from 'react'
import {
  AdminSchedulableStudentsDocument,
  CoachingAttendeeStatus,
  UpdateCoachingAppointmentDocument,
} from '../gql/graphql'

gql`
  mutation UpdateCoachingAppointment(
    $sessionId: UUID!
    $students: [CoachingStudentInput!]!
  ) {
    updateCoachingAppointment(sessionId: $sessionId, students: $students) {
      ... on GraphqlSuccess {
        success
      }
      ... on GraphqlRequestError {
        code
        message
      }
    }
  }
`

export interface RosterAttendee {
  status: CoachingAttendeeStatus
  headcount: number
  student: { id?: string | null; name?: string | null; mobile?: string | null }
}

interface Candidate {
  userId: string
  name: string
  mobile?: string | null
  /** Hours the student could put into this appointment, own seat included. */
  availableHours: number
  validUntil?: string
  status?: CoachingAttendeeStatus
}

const STATUS_LABEL: Partial<Record<CoachingAttendeeStatus, string>> = {
  [CoachingAttendeeStatus.Scheduled]: '待确认',
  [CoachingAttendeeStatus.Confirmed]: '已确认',
}

interface EditRosterDialogProps {
  sessionId: string
  courseId: string
  seats: number
  /** yyyy-MM-dd of the appointment's slots. */
  date: string
  sessionHours: number
  attendees: RosterAttendee[]
  onClose: () => void
  onSuccess: () => void
}

export default function EditRosterDialog({
  sessionId,
  courseId,
  seats,
  date,
  sessionHours,
  attendees,
  onClose,
  onSuccess,
}: EditRosterDialogProps) {
  const live = attendees.filter(
    (a) => a.status !== CoachingAttendeeStatus.Cancelled && a.student.id,
  )
  // Start from the current roster.
  const [selectedIds, setSelectedIds] = useState<string[]>(() =>
    live.map((a) => a.student.id as string),
  )
  const [headcounts, setHeadcounts] = useState<Record<string, number>>(() =>
    Object.fromEntries(live.map((a) => [a.student.id as string, a.headcount])),
  )

  const { data: studentsData, loading: studentsLoading } = useQuery(
    AdminSchedulableStudentsDocument,
    {
      variables: { courseId, date, hours: sessionHours },
      fetchPolicy: 'network-only',
    },
  )

  const [updateCoachingAppointment, { loading: submitting }] = useMutation(
    UpdateCoachingAppointmentDocument,
  )

  // A current attendee's own hours are already held or spent by this
  // appointment, so `schedulableStudents` leaves them out of availableHours
  // (or the student out entirely); add them back.
  const schedulable = new Map(
    (studentsData?.schedulableStudents ?? []).flatMap((s) =>
      s.student.id ? [[s.student.id, s] as const] : [],
    ),
  )
  const candidates: Candidate[] = [
    ...live.map((a) => {
      const id = a.student.id as string
      const s = schedulable.get(id)
      return {
        userId: id,
        name: a.student.name ?? '未知',
        mobile: a.student.mobile,
        availableHours: (s?.availableHours ?? 0) + a.headcount * sessionHours,
        validUntil: s?.validUntil,
        status: a.status,
      }
    }),
    ...[...schedulable.values()]
      .filter((s) => !live.some((a) => a.student.id === s.student.id))
      .map((s) => ({
        userId: s.student.id as string,
        name: s.student.name ?? '未知',
        mobile: s.student.mobile,
        availableHours: s.availableHours,
        validUntil: s.validUntil,
      })),
  ]

  const headcountOf = (id: string) => headcounts[id] ?? 1
  const sumHeadcounts = (ids: string[]) =>
    ids.reduce((sum, id) => sum + headcountOf(id), 0)
  const totalHeadcount = sumHeadcounts(selectedIds)
  // A 1:1 course has no room for a companion, so no stepper at all.
  const showHeadcount = seats > 1

  const toggleStudent = (studentId: string) => {
    if (selectedIds.includes(studentId)) {
      setSelectedIds(selectedIds.filter((id) => id !== studentId))
      return
    }
    // 1:1: picking another student replaces the current one, like a radio.
    if (seats === 1) {
      setSelectedIds([studentId])
      return
    }
    const next = [...selectedIds, studentId]
    if (sumHeadcounts(next) > seats) {
      message.error(`最多选择 ${seats} 人`)
      return
    }
    setSelectedIds(next)
  }

  const changed =
    selectedIds.length !== live.length ||
    live.some(
      (a) =>
        !selectedIds.includes(a.student.id as string) ||
        headcountOf(a.student.id as string) !== a.headcount,
    )
  const canSubmit = changed && selectedIds.length > 0 && totalHeadcount <= seats

  const submit = async () => {
    try {
      const result = await updateCoachingAppointment({
        variables: {
          sessionId,
          students: selectedIds.map((studentId) => ({
            studentId,
            headcount: headcountOf(studentId),
          })),
        },
      })
      const res = result.data?.updateCoachingAppointment
      if (res?.__typename === 'GraphqlRequestError') {
        message.error(res.message || '修改失败')
        return
      }
      message.success('已修改')
      onSuccess()
    } catch (e) {
      console.error('Failed to modify appointment:', e)
      message.error('修改失败')
    }
  }

  const handleSubmit = () => {
    if (!canSubmit) return
    const removedConfirmed = live.filter(
      (a) =>
        a.status === CoachingAttendeeStatus.Confirmed &&
        !selectedIds.includes(a.student.id as string),
    )
    if (removedConfirmed.length === 0) {
      submit()
      return
    }
    const names = removedConfirmed.map((a) => a.student.name ?? '学员').join('、')
    message.prompt(`${names} 已确认预约，移除后课时将退回，并短信通知。确定吗？`, {
      title: '确认移除',
      noLabel: '返回',
      yesLabel: '确认移除',
      onYesButton: submit,
    })
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>修改学员</DialogTitle>
      <DialogContent dividers>
        {studentsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <List dense disablePadding>
            {candidates.map((student) => {
              const selected = selectedIds.includes(student.userId)
              const headcount = headcountOf(student.userId)
              const max = maxHeadcountFor({
                seats,
                totalHeadcount,
                ownHeadcount: headcount,
                availableHours: student.availableHours,
                sessionHours,
              })
              return (
                <ListItem
                  key={student.userId}
                  disableGutters
                  secondaryAction={
                    showHeadcount &&
                    selected &&
                    hoursCapOf(student.availableHours, sessionHours) > 1 && (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ mr: 1 }}>
                          人数 (含本人)
                        </Typography>
                        <IconButton
                          size="small"
                          aria-label="减少人数"
                          disabled={headcount <= 1}
                          onClick={() =>
                            setHeadcounts({
                              ...headcounts,
                              [student.userId]: headcount - 1,
                            })
                          }
                        >
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                        <Typography sx={{ minWidth: 20, textAlign: 'center' }}>
                          {headcount}
                        </Typography>
                        <IconButton
                          size="small"
                          aria-label="增加人数"
                          disabled={headcount >= max}
                          onClick={() =>
                            setHeadcounts({
                              ...headcounts,
                              [student.userId]: headcount + 1,
                            })
                          }
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )
                  }
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selected}
                        onChange={() => toggleStudent(student.userId)}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">
                          {student.name}
                          {student.mobile && ` · ${student.mobile}`}
                          {student.status && ` · ${STATUS_LABEL[student.status]}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          可约 {student.availableHours} 课时
                          {student.validUntil && ` · 有效期至 ${student.validUntil}`}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              )
            })}
          </List>
        )}
        <Typography variant="body2" color="text.secondary">
          已选择 {totalHeadcount} 人 / 最多 {seats} 人
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>返回</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit || submitting}
        >
          保存修改
        </Button>
      </DialogActions>
    </Dialog>
  )
}
